// src/utils/contractRoles.js
// Which people on a listing belong to the BUYER'S side of its contract.
//
// When a contract falls through (src/utils/contractFellThrough.js), these
// participants come off the listing so the next buyer's people can be added.
// They are saved in the Previous Contracts history first, so nothing is lost,
// and any role that does not match here stays on the listing.
//
// Role names are data, not code: they live in the live contact_type collection
// (Settings > Contacts > Contact Type) and are copied onto each deal's
// contactType entries as `type`. Names are compared after normalizing, so
// "Buyer's Agent", "Buyers Agent" and "buyer agent" all count.
// Live contact_type names read from team-edelstein on 09/15/2026: Appraiser, Buyers Agent,
// Buyer's Closer, Co-Buyers Agent, Co-Listing Agent, Listing Agent, Lender, Seller's Closer,
// Transaction Coordinator. Come off: Buyers Agent, Co-Buyers Agent, Lender, Buyer's Closer.
// Henry chose to keep the Appraiser on the listing.

// Lowercase, drop apostrophes (straight and curly), turn every other
// punctuation run into one space.
export const normalizeRoleName = (typeName) =>
  String(typeName ?? "")
    .toLowerCase()
    .replace(/['‘’`]/g, "")
    .replace(/[^a-z0-9]+/g, " ")
    .trim();

// Matched against normalizeRoleName(typeName). Keep these non-global (no /g),
// so .test() never carries state between calls.
export const BUYER_SIDE_ROLE_PATTERNS = [
  // The buyer's agent: "Buyer's Agent", "Buyers Agent", "Buyer Side Agent"
  /\bbuyers? (side )?agent\b/,
  // The buyer's lender: "Lender", "Mortgage Lender", "Loan Officer", "Mortgage Broker"
  /\blender\b/,
  /\bloan officer\b/,
  /\bmortgage\b/,
  // The other side's title: "Other Side Title Company", "Other-Side Title Closer",
  // "Other Title Company", "Other Side Closer", "Buyer's Title Company", "Buyers Closer"
  /\bother (side )?title\b/,
  /\bother (side )?closer\b/,
  /\bbuyers? (side )?title\b/,
  /\bbuyers? (side )?closer\b/,
];

export function isBuyerSideRole(typeName) {
  const name = normalizeRoleName(typeName);
  if (!name) return false;
  return BUYER_SIDE_ROLE_PATTERNS.some((pattern) => pattern.test(name));
}
